import { formatMetricString } from "./commonHelper";

export interface MetricsLinkEntry {
  link: string;
  widgetName: string | null;
  tabMetrics: { tabId: string; tabLabel: string; metricId: string }[];
  exchangeMetricIds: string[];
}

export interface CrossSectionTarget {
  section: "widget" | "tab" | "exchange";
  label: string;
  metricId: string;
  detail?: string;
}

const metricsLinkMap: MetricsLinkEntry[] = [
  {
    link: "impressions",
    widgetName: "Impressions",
    tabMetrics: [
      { tabId: "creative", tabLabel: "Creative Data", metricId: "impressions" },
      { tabId: "keywords", tabLabel: "Keyword Data", metricId: "impressions" },
      { tabId: "databy", tabLabel: "Data By Period", metricId: "impressions" },
    ],
    exchangeMetricIds: ["Served__c"],
  },
  {
    link: "clicks",
    widgetName: "Clicks",
    tabMetrics: [
      { tabId: "creative", tabLabel: "Creative Data", metricId: "clicks" },
      { tabId: "keywords", tabLabel: "Keyword Data", metricId: "clicks" },
      { tabId: "databy", tabLabel: "Data By Period", metricId: "clicks" },
    ],
    exchangeMetricIds: ["Clicks__c"],
  },
  {
    link: "ctr",
    widgetName: "AVG. CTR",
    tabMetrics: [
      { tabId: "creative", tabLabel: "Creative Data", metricId: "ctr" },
      { tabId: "keywords", tabLabel: "Keyword Data", metricId: "calc_ctr" },
      { tabId: "databy", tabLabel: "Data By Period", metricId: "ctr" },
    ],
    exchangeMetricIds: ["ctr"],
  },
  {
    link: "completedViews",
    widgetName: null,
    tabMetrics: [
      { tabId: "creative", tabLabel: "Creative Data", metricId: "completed_views" },
      { tabId: "databy", tabLabel: "Data By Period", metricId: "completed_views" },
    ],
    exchangeMetricIds: ["Completed_Views__c"],
  },
  {
    link: "completionRate",
    widgetName: null,
    tabMetrics: [
      { tabId: "creative", tabLabel: "Creative Data", metricId: "completion_rate" },
      { tabId: "databy", tabLabel: "Data By Period", metricId: "completion_rate" },
    ],
    exchangeMetricIds: ["completion_rate"],
  },
  {
    link: "conversions",
    widgetName: null,
    tabMetrics: [
      { tabId: "creative", tabLabel: "Creative Data", metricId: "conversions" },
      { tabId: "keywords", tabLabel: "Keyword Data", metricId: "conversions" },
      { tabId: "databy", tabLabel: "Data By Period", metricId: "conversions" },
    ],
    exchangeMetricIds: ["Conversions__c"],
  },
  {
    link: "estSpend",
    widgetName: "Est. Spend",
    tabMetrics: [
      { tabId: "creative", tabLabel: "Creative Data", metricId: "estimate_spend" },
      { tabId: "databy", tabLabel: "Data By Period", metricId: "estimate_spend" },
    ],
    exchangeMetricIds: ["estimated_spend"],
  },
  {
    link: "cpc",
    widgetName: "AVG. CPC",
    tabMetrics: [
      { tabId: "keywords", tabLabel: "Keyword Data", metricId: "calc_avg_cpc" },
    ],
    exchangeMetricIds: ["cpc"],
  },
  {
    link: "cpa",
    widgetName: "AVG. CPA",
    tabMetrics: [],
    exchangeMetricIds: ["cpa"],
  },
  {
    link: "cvr",
    widgetName: "AVG. Conv. Rate",
    tabMetrics: [],
    exchangeMetricIds: ["cvr"],
  },
  {
    link: "cost",
    widgetName: null,
    tabMetrics: [
      { tabId: "keywords", tabLabel: "Keyword Data", metricId: "cost" },
    ],
    exchangeMetricIds: ["Cost__c"],
  },
];

export const findLinkByWidget = (widgetName: string): MetricsLinkEntry | undefined => {
  return metricsLinkMap.find(entry => entry.widgetName === widgetName);
};

export const findLinkByTabMetric = (metricId: string): MetricsLinkEntry | undefined => {
  return metricsLinkMap.find(entry =>
    entry.tabMetrics.some(tm => tm.metricId === metricId)
  );
};

export const findLinkByExchangeMetric = (metricId: string): MetricsLinkEntry | undefined => {
  const normalized = formatMetricString(metricId);
  return metricsLinkMap.find(entry =>
    entry.exchangeMetricIds.some(id => formatMetricString(id) === normalized)
  );
};

export const getCrossSectionTargets = (
  link: MetricsLinkEntry,
  originSection: "widget" | "tab" | "exchange",
  originDetail?: string
): CrossSectionTarget[] => {
  const targets: CrossSectionTarget[] = [];

  if (originSection !== "widget" && link.widgetName) {
    targets.push({
      section: "widget",
      label: `Analytics Widgets — ${link.widgetName}`,
      metricId: link.widgetName,
    });
  }

  link.tabMetrics.forEach(tm => {
    if (originSection === "tab" && originDetail === tm.tabId) return;
    targets.push({
      section: "tab",
      label: `Report Tabs — ${tm.tabLabel}`,
      metricId: tm.metricId,
      detail: tm.tabId,
    });
  });

  if (link.exchangeMetricIds.length > 0) {
    targets.push({
      section: "exchange",
      label: "All Exchange Metrics",
      metricId: link.exchangeMetricIds[0],
    });
  }

  return targets;
};
